import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "./auth";

type TenantCtx = {
  /** The effective tenant_id used for API calls. May differ from the JWT/demo tenant if the user has switched. */
  effectiveTenantId: string | null;
  /** Berater-side switch (only platform_admin can change). */
  setEffectiveTenantId: (id: string) => void;
  /** Detect whether current user is allowed to switch tenants. */
  canSwitch: boolean;
};

const Ctx = createContext<TenantCtx | null>(null);

const STORAGE_KEY = "kpi.effectiveTenantId";
const PLATFORM_ADMIN_ROLE = "platform_admin";

export function canUseTenantOverride(roles: string[]): boolean {
  return roles.includes(PLATFORM_ADMIN_ROLE);
}

export function resolveEffectiveTenantId({
  authTenantId,
  overrideTenantId,
  canSwitch,
}: {
  authTenantId: string | null;
  overrideTenantId: string | null;
  canSwitch: boolean;
}): string | null {
  return canSwitch ? overrideTenantId ?? authTenantId : authTenantId;
}

export function shouldClearTenantOverride({
  authTenantId,
  canSwitch,
}: {
  authTenantId: string | null;
  canSwitch: boolean;
}): boolean {
  return !authTenantId || !canSwitch;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { tenantId, roles } = useAuth();
  const canSwitch = canUseTenantOverride(roles);
  const [override, setOverride] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(STORAGE_KEY);
  });

  // Tenant overrides are only valid for platform admins. Non-admin users must
  // never inherit a stale sessionStorage override from a previous session.
  useEffect(() => {
    if (shouldClearTenantOverride({ authTenantId: tenantId, canSwitch })) {
      setOverride(null);
      if (typeof window !== "undefined") window.sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [canSwitch, tenantId]);

  const effective = resolveEffectiveTenantId({
    authTenantId: tenantId,
    overrideTenantId: override,
    canSwitch,
  });

  const setEffectiveTenantId = (id: string) => {
    if (!canSwitch) return;
    setOverride(id);
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(STORAGE_KEY, id);
    }
  };

  return (
    <Ctx.Provider value={{ effectiveTenantId: effective, setEffectiveTenantId, canSwitch }}>
      {children}
    </Ctx.Provider>
  );
}

export function useTenant(): TenantCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTenant outside TenantProvider");
  return v;
}
